import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import {
  MetricActions,
  PatientEstimateActions,
  PatientEstimateService,
  patientEstimateIsFailureSelector,
  patientEstimateIsPendingSelector,
  patientEstimateVerifyErrorSelector,
  patientEstimateVerifyResponseSelector,
} from '@patient-ui/patient-web/store';
import {
  ZIP_REGEX,
  patientEstimateVerifyValidationMessages,
} from '@patient-ui/shared/constants';
import {
  EventCode,
  PatientEstimateVerifyRequest,
  PatientEstimateVerifyResponse,
} from '@patient-ui/shared/models';
import { SplitService } from '@patient-ui/shared-ui/utils';

import { dateValidator } from '../validators/date.validator';

@Component({
  selector: 'patient-ui-patient-estimate-verify',
  templateUrl: './patient-estimate-verify.component.html',
  styleUrls: ['./patient-estimate-verify.component.scss'],
})
export class PatientEstimateVerifyComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  readonly validationMessages = patientEstimateVerifyValidationMessages;

  minDate = new Date();
  maxDate = new Date();

  patientForm!: UntypedFormGroup;
  btnGetEstimateSpin = false;
  showAlert$ = new BehaviorSubject<boolean>(false);
  alertStatus: 'danger' | 'warning' | 'info' = 'danger';
  errorMsg = '';
  shortKey = '';

  // Feature flag
  patientEstimateEnabled$: Observable<boolean>;

  // Capture form values at submission time to prevent race conditions
  private submittedDob = '';
  private submittedZip = '';

  // Store blob URL for cleanup - keep alive until component destroys
  private currentBlobUrl?: string;

  // Store verification response data for metrics
  private verificationResponse?: PatientEstimateVerifyResponse;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private store: Store,
    private patientEstimateService: PatientEstimateService,
    private splitService: SplitService,
    private cdr: ChangeDetectorRef
  ) {
    // Initialize feature flag (default to true for backward compatibility)
    this.patientEstimateEnabled$ = this.splitService.isOn$(
      'patientEstimateOn',
      true
    );
  }

  ngOnInit(): void {
    // Check feature flag and redirect if disabled
    this.patientEstimateEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEnabled) => {
        if (!isEnabled) {
          // Redirect to home page if feature is disabled
          void this.router.navigate(['/']);
          return;
        }
      });

    // Set date range: 0-120 years (current date to 120 years ago)
    this.maxDate.setFullYear(this.maxDate.getFullYear());
    this.minDate.setFullYear(this.minDate.getFullYear() - 120);

    // Get the shortKey from the URL parameter
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.shortKey = params.get('shortKey') || '';

        // Log page load metric after shortKey is available
        if (this.shortKey) {
          this.logPageLoaded();
        }
      });

    this.initForm();
    this.setupStoreSubscriptions();
  }

  ngOnDestroy(): void {
    this.store.dispatch(PatientEstimateActions.ClearPatientEstimateState());
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up blob URL to prevent memory leak
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = undefined;
    }
  }

  private setupStoreSubscriptions(): void {
    // Subscribe to loading state
    this.store
      .select(patientEstimateIsPendingSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isPending) => {
        this.btnGetEstimateSpin = isPending;
      });

    // Subscribe to verify response
    this.store
      .select(patientEstimateVerifyResponseSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response) {
          this.handleSuccessResponse(response);
        }
      });

    // Subscribe to error state
    this.store
      .select(patientEstimateIsFailureSelector)
      .pipe(
        filter((isFailure) => isFailure),
        switchMap(() => this.store.select(patientEstimateVerifyErrorSelector)),
        takeUntil(this.destroy$)
      )
      .subscribe((errorMessage) => {
        if (errorMessage) {
          this.handleErrorResponse(errorMessage);
        }
      });
  }

  private handleSuccessResponse(response: PatientEstimateVerifyResponse): void {
    // Store response for metrics
    this.verificationResponse = response;

    // Re-enable form after verification completes
    this.patientForm.enable();

    // Backend returns { success: true }
    // Check for either verified or success flag
    if (response.verified || response.success) {
      // Log verification success metric
      this.logVerificationSuccess(response);

      // Use direct PDF streaming endpoint: POST /guest/estimate/{token}/pdf
      this.openPdfInNewWindow();
    } else {
      this.showAlert$.next(true);
      this.alertStatus = 'danger';
      this.errorMsg =
        "We couldn't find your information. Check your entry and try again.";
    }
    window.scroll(0, 0);
  }

  private openPdfInNewWindow(): void {
    // Use captured values from submission to prevent race condition
    const dob = this.submittedDob;
    const zip = this.submittedZip;

    // Immediately stop the spinner since verification is complete
    // The store already updated to Success, but we need to ensure UI updates before PDF fetch
    this.btnGetEstimateSpin = false;

    // Force Angular to detect the change immediately
    this.cdr.detectChanges();

    // Defer PDF fetch to next tick to ensure the spinner UI has updated
    setTimeout(() => {
      // Fetch PDF blob from service
      this.patientEstimateService
        .fetchPdfBlob(this.shortKey, dob, zip)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (blob) => {
            // Validate blob size first before any operations
            if (blob.size === 0) {
              this.showAlert$.next(true);
              this.alertStatus = 'danger';
              this.errorMsg = 'Received empty PDF file. Please try again.';
              return;
            }

            // Revoke previous blob URL if it exists to prevent memory leak
            if (this.currentBlobUrl) {
              URL.revokeObjectURL(this.currentBlobUrl);
            }

            // Ensure blob has correct MIME type for PDF (following order-details pattern)
            const pdfBlob = new Blob([blob], { type: 'application/pdf' });

            const blobUrl = URL.createObjectURL(pdfBlob);
            this.currentBlobUrl = blobUrl;

            // Open PDF in new tab (following order-details pattern)
            const pdfWindow = window.open(blobUrl, '_blank');

            if (pdfWindow) {
              // Log PDF window opened metric
              this.logPdfViewed();
            } else {
              // Window failed to open - revoke immediately and show error
              URL.revokeObjectURL(blobUrl);
              this.currentBlobUrl = undefined;
              this.showAlert$.next(true);
              this.alertStatus = 'warning';
              this.errorMsg =
                'Unable to open PDF. Please check your browser settings and allow popups for this site.';
            }
          },
          error: (error) => {
            this.showAlert$.next(true);
            this.alertStatus = 'danger';

            // Set default error message first
            this.errorMsg = 'Failed to load PDF. Please try again.';

            // Try to extract error message from backend response
            if (error.error && typeof error.error === 'object') {
              // If error.error is a Blob, we need to read it asynchronously
              if (error.error instanceof Blob) {
                this.parseErrorBlob(error.error).then((backendError) => {
                  // Guard against component destruction during async operation
                  if (this.destroy$.closed) {
                    return;
                  }
                  if (backendError) {
                    this.errorMsg = backendError;
                    this.cdr.detectChanges();
                  }
                });
              } else {
                // Direct JSON error object
                const backendError = error.error.error;
                if (backendError) {
                  this.errorMsg = backendError;
                }
              }
            }
          },
        });
    }, 0); // Defer to next tick
  }

  private async parseErrorBlob(errorBlob: Blob): Promise<string | null> {
    try {
      const text = await errorBlob.text();
      const errorJson = JSON.parse(text);
      return errorJson.error || null;
    } catch (e) {
      return null;
    }
  }

  private handleErrorResponse(errorMessage: string): void {
    // Log verification failed metric
    this.logVerificationFailed(errorMessage);

    // Re-enable form after error
    this.patientForm.enable();

    this.showAlert$.next(true);
    this.alertStatus = 'danger';
    this.errorMsg = errorMessage;
    window.scroll(0, 0);
  }

  initForm(): void {
    this.patientForm = new UntypedFormGroup(
      {
        dateOfBirth: new UntypedFormControl('', [
          Validators.required,
          dateValidator(this.maxDate, this.minDate),
        ]),
        zipCode: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern(ZIP_REGEX),
        ]),
      },
      { updateOn: 'blur' }
    );
  }

  onSubmit(): void {
    if (!this.patientForm.valid) {
      // Mark the form as dirty in case what's invalid is forgetting to populate a field
      Object.keys(this.patientForm.controls).forEach((key) => {
        this.patientForm.controls[key].markAsDirty();
      });
      this.patientForm.updateValueAndValidity();
      this.showAlert$.next(false);
      return;
    }

    this.performFormSubmission();
  }

  private performFormSubmission(): void {
    this.showAlert$.next(false);

    const dob = this.formatDate(this.patientForm.get('dateOfBirth')?.value);
    const zip = this.patientForm.get('zipCode')?.value;

    // Capture values at submission time to prevent race condition
    this.submittedDob = dob;
    this.submittedZip = zip;

    // Log verification attempted metric
    this.logVerificationAttempted();

    // Disable form to prevent changes during verification
    this.patientForm.disable();

    const payload: PatientEstimateVerifyRequest = {
      shortKey: this.shortKey,
      dateOfBirth: dob,
      zipCode: zip,
    };

    this.store.dispatch(
      PatientEstimateActions.VerifyPatientEstimate({ payload })
    );
  }

  private formatDate(dateString: string): string {
    // Convert from MMDDYYYY to YYYY-MM-DD
    return dateString
      ? dateString.substring(4) +
          '-' +
          dateString.substring(0, 2) +
          '-' +
          dateString.substring(2, 4)
      : '';
  }

  // ============================================================================
  // Metric Tracking Methods
  // ============================================================================

  private logPageLoaded(): void {
    // Metric: Log page load
    this.store.dispatch(
      MetricActions.logEstimateVerificationMetric({
        metrics: {
          eventCode: EventCode.estimateVerificationPageLoaded,
          shortKey: this.shortKey,
          userType: 'Guest',
        },
      })
    );
  }

  private logVerificationAttempted(): void {
    // Metric: Log verification attempted
    this.store.dispatch(
      MetricActions.logEstimateVerificationMetric({
        metrics: {
          eventCode: EventCode.estimateVerificationAttempted,
          shortKey: this.shortKey,
          userType: 'Guest',
        },
      })
    );
  }

  private logVerificationSuccess(
    response: PatientEstimateVerifyResponse
  ): void {
    const lpid = response.lpid;
    const accessionNumber = response.accessionNumber;

    // Only log if we have valid lpid/accessionNumber to avoid polluting analytics with undefined
    if (!lpid || !accessionNumber) {
      return;
    }

    // Metric: Log verification success
    this.store.dispatch(
      MetricActions.logEstimateVerificationMetric({
        metrics: {
          eventCode: EventCode.estimateVerificationSuccess,
          shortKey: this.shortKey,
          userType: 'Guest',
          lpId: lpid,
          accessionNumber,
          statusCode: 200,
        },
      })
    );
  }

  private logVerificationFailed(errorMessage: string): void {
    // Metric: Log verification failed
    this.store.dispatch(
      MetricActions.logEstimateVerificationMetric({
        metrics: {
          eventCode: EventCode.estimateVerificationFailed,
          shortKey: this.shortKey,
          userType: 'Guest',
          alertMsg: errorMessage,
          statusCode: 400,
        },
      })
    );
  }

  private logPdfViewed(): void {
    const lpid = this.verificationResponse?.lpid;
    const accessionNumber = this.verificationResponse?.accessionNumber;

    // Only log if we have valid lpid/accessionNumber to avoid polluting analytics with undefined
    if (!lpid || !accessionNumber) {
      return;
    }

    // Metric: Log PDF viewed
    this.store.dispatch(
      MetricActions.logEstimateVerificationMetric({
        metrics: {
          eventCode: EventCode.estimatePdfViewed,
          shortKey: this.shortKey,
          userType: 'Guest',
          lpId: lpid,
          accessionNumber,
        },
      })
    );
  }
}
